import * as React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Box, Download, Trash2 } from 'lucide-react';
import { cn } from '../lib/utils.js';
import type { App, LogEntry, SearchParams, ExportFormat } from '../lib/api.js';
import { Button } from './ui/button.js';
import { Select } from './ui/select.js';
import { SearchBar } from './SearchBar.js';
import { LogTable } from './LogTable.js';
import { useLogSearchParams } from '../hooks/useLogSearchParams.js';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './ui/dialog.js';

const PAGE_SIZE_OPTIONS = [
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '250', label: '250' },
];

const EXPORT_FORMAT_OPTIONS = [
  { value: 'json', label: 'JSON' },
  { value: 'txt', label: 'Text' },
  { value: 'csv', label: 'CSV' },
];

export interface LogViewerProps {
  /** Available apps to search */
  apps?: App[];
  /** Currently selected app ID */
  selectedAppId?: string | null;
  /** Callback when app selection changes */
  onSelectApp?: (appId: string | null) => void;
  /** Array of log entries to display */
  logs: LogEntry[];
  /** Total number of logs matching the search */
  total: number;
  /** Whether there are more logs to fetch */
  hasMore: boolean;
  /** Current page (1-indexed) */
  page?: number;
  /** Page size */
  pageSize?: number;
  /** Callback when search parameters change */
  onSearch: (params: SearchParams, appId: string) => void;
  /** Callback when page changes */
  onPageChange?: (page: number) => void;
  /** Callback when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
  /** Callback when export is requested */
  onExport?: (appId: string, format: ExportFormat, params: SearchParams) => Promise<void>;
  /** Callback when clear logs is requested */
  onClearLogs?: (appId: string, params?: SearchParams) => Promise<void>;
  /** Whether the viewer is loading */
  isLoading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Additional class name */
  className?: string;
  /** Default date range in hours */
  defaultRangeHours?: number;
}

/**
 * LogViewer combines SearchBar, LogTable, and pagination controls.
 * Syncs filter state with URL for shareable links and browser navigation.
 */
export function LogViewer({
  apps = [],
  selectedAppId,
  onSelectApp,
  logs,
  total,
  hasMore,
  page = 1,
  pageSize = 100,
  onSearch,
  onPageChange,
  onPageSizeChange,
  onExport,
  onClearLogs,
  isLoading = false,
  error = null,
  className,
  defaultRangeHours = 24,
}: LogViewerProps) {
  // URL state management
  const {
    urlSearchParams,
    urlAppId,
    urlPage,
    urlPageSize,
    setUrlSearchParams,
    clearUrlParams,
  } = useLogSearchParams({ defaultRangeHours });

  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  // currentSearchParams = for API calls (has computed from/to defaults)
  const [currentSearchParams, setCurrentSearchParams] = React.useState<SearchParams | null>(null);
  // userEnteredParams = what user actually entered (no computed defaults) - used for URL
  const [userEnteredParams, setUserEnteredParams] = React.useState<Partial<SearchParams> | null>(null);
  const [localSelectedAppId, setLocalSelectedAppId] = React.useState<string>(
    urlAppId ?? selectedAppId ?? apps[0]?.id ?? ''
  );
  const [exportFormat, setExportFormat] = React.useState<ExportFormat>('json');
  const [showClearDialog, setShowClearDialog] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [isClearing, setIsClearing] = React.useState(false);
  const [initialLoadDone, setInitialLoadDone] = React.useState(false);

  // Sync page/pageSize from URL on mount
  React.useEffect(() => {
    if (urlPage !== page) {
      onPageChange?.(urlPage);
    }
    if (urlPageSize !== pageSize) {
      onPageSizeChange?.(urlPageSize);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Sync with external selection
  React.useEffect(() => {
    if (selectedAppId !== undefined && selectedAppId !== null) {
      setLocalSelectedAppId(selectedAppId);
    }
  }, [selectedAppId]);

  // Update local selection when apps change and no selection
  React.useEffect(() => {
    if (!localSelectedAppId && apps[0]?.id) {
      setLocalSelectedAppId(apps[0].id);
    }
  }, [apps, localSelectedAppId]);

  // Ref to track the last processed URL to avoid re-processing
  const lastProcessedUrlRef = React.useRef<string | null>(null);

  // Auto-execute search from URL params on initial load
  React.useEffect(() => {
    if (initialLoadDone) return;
    if (!apps.length) return; // Wait for apps to load

    const appId = urlAppId || localSelectedAppId || apps[0]?.id;
    if (!appId) return;

    // Check if URL has ANY search params that warrant auto-search
    const hasAnySearchParams = urlSearchParams && (
      urlSearchParams.from ||
      urlSearchParams.to ||
      urlSearchParams.match ||
      urlSearchParams.regex ||
      urlSearchParams.levels?.length ||
      urlSearchParams.sub_apps?.length ||
      urlSearchParams.instance_ids?.length ||
      urlSearchParams.session_ids?.length ||
      urlSearchParams.correlation_ids?.length
    );

    // If URL has search params, execute the search
    if (hasAnySearchParams) {
      // Use defaults for from/to if not specified
      const now = new Date();
      const defaultFrom = new Date(now.getTime() - defaultRangeHours * 60 * 60 * 1000);

      const params: SearchParams = {
        from: urlSearchParams.from || defaultFrom.toISOString(),
        to: urlSearchParams.to || now.toISOString(),
        levels: urlSearchParams.levels,
        sub_apps: urlSearchParams.sub_apps,
        instance_ids: urlSearchParams.instance_ids,
        session_ids: urlSearchParams.session_ids,
        correlation_ids: urlSearchParams.correlation_ids,
        match: urlSearchParams.match,
        regex: urlSearchParams.regex,
        limit: urlPageSize,
        offset: (urlPage - 1) * urlPageSize,
      };

      // Update local state
      if (urlAppId && urlAppId !== localSelectedAppId) {
        setLocalSelectedAppId(urlAppId);
        onSelectApp?.(urlAppId);
      }
      setCurrentSearchParams(params);
      // URL params are user-entered (came from URL)
      setUserEnteredParams(urlSearchParams);

      // Mark this URL as processed to prevent the sync effect from re-triggering
      lastProcessedUrlRef.current = JSON.stringify({
        ...urlSearchParams,
        appId: urlAppId,
        page: urlPage,
      });

      // Execute search
      onSearch(params, appId);
      setInitialLoadDone(true);
    } else {
      setInitialLoadDone(true);
    }
  }, [apps, urlSearchParams, urlAppId, urlPage, urlPageSize, initialLoadDone, localSelectedAppId, onSearch, onSelectApp, defaultRangeHours]);

  // Sync URL when search params change from URL navigation (back/forward)

  React.useEffect(() => {
    if (!initialLoadDone) return;
    if (!apps.length) return;

    const appId = urlAppId || localSelectedAppId;
    if (!appId) return;

    // Check if URL has ANY search params
    const hasAnySearchParams = urlSearchParams && (
      urlSearchParams.from ||
      urlSearchParams.to ||
      urlSearchParams.match ||
      urlSearchParams.regex ||
      urlSearchParams.levels?.length ||
      urlSearchParams.sub_apps?.length ||
      urlSearchParams.instance_ids?.length ||
      urlSearchParams.session_ids?.length ||
      urlSearchParams.correlation_ids?.length
    );

    // Create a stable key from URL params (user-entered values only)
    const urlKey = hasAnySearchParams
      ? JSON.stringify({
          ...urlSearchParams,
          appId: urlAppId,
          page: urlPage,
        })
      : null;

    // Skip if we've already processed this exact URL
    if (urlKey === lastProcessedUrlRef.current) {
      return;
    }

    if (hasAnySearchParams && urlKey) {
      // URL changed (e.g., back/forward button), sync state
      const now = new Date();
      const defaultFrom = new Date(now.getTime() - defaultRangeHours * 60 * 60 * 1000);

      const params: SearchParams = {
        from: urlSearchParams.from || defaultFrom.toISOString(),
        to: urlSearchParams.to || now.toISOString(),
        levels: urlSearchParams.levels,
        sub_apps: urlSearchParams.sub_apps,
        instance_ids: urlSearchParams.instance_ids,
        session_ids: urlSearchParams.session_ids,
        correlation_ids: urlSearchParams.correlation_ids,
        match: urlSearchParams.match,
        regex: urlSearchParams.regex,
        limit: urlPageSize,
        offset: (urlPage - 1) * urlPageSize,
      };

      if (urlAppId && urlAppId !== localSelectedAppId) {
        setLocalSelectedAppId(urlAppId);
        onSelectApp?.(urlAppId);
      }
      setCurrentSearchParams(params);
      setUserEnteredParams(urlSearchParams);
      onPageChange?.(urlPage);

      // Mark this URL as processed BEFORE calling onSearch to prevent loops
      lastProcessedUrlRef.current = urlKey;
      onSearch(params, urlAppId || localSelectedAppId);
    } else if (!hasAnySearchParams && lastProcessedUrlRef.current) {
      // URL cleared, reset state
      lastProcessedUrlRef.current = null;
      setCurrentSearchParams(null);
      setUserEnteredParams(null);
    }
  }, [urlSearchParams, urlAppId, urlPage, urlPageSize, apps, initialLoadDone, localSelectedAppId, defaultRangeHours, onSearch, onSelectApp, onPageChange]);

  const appOptions = apps.map((app) => ({
    value: app.id,
    label: app.name,
  }));

  const totalPages = Math.ceil(total / pageSize);
  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, total);

  const handleSearch = (params: Partial<SearchParams>) => {
    if (!localSelectedAppId) {
      return; // Can't search without an app
    }

    // Save what user actually entered (for URL updates later)
    setUserEnteredParams(params);

    // Compute defaults for API call if from/to not provided
    const now = new Date();
    const defaultFrom = new Date(now.getTime() - defaultRangeHours * 60 * 60 * 1000);

    const paramsForApi: SearchParams = {
      ...params,
      from: params.from || defaultFrom.toISOString(),
      to: params.to || now.toISOString(),
      limit: pageSize,
      offset: 0,
    };

    setCurrentSearchParams(paramsForApi);
    setExpandedId(null);
    onSearch(paramsForApi, localSelectedAppId);
    onPageChange?.(1);

    // Update URL with only user-entered values (not computed defaults)
    setUrlSearchParams(params as SearchParams, localSelectedAppId, 1, pageSize);
  };

  const handleAppChange = (appId: string) => {
    setLocalSelectedAppId(appId);
    onSelectApp?.(appId);
    // Reset search when app changes
    setCurrentSearchParams(null);
    setUserEnteredParams(null);
    setExpandedId(null);
    // Clear URL params when app changes
    clearUrlParams();
  };

  const handleReset = () => {
    setCurrentSearchParams(null);
    setUserEnteredParams(null);
    setExpandedId(null);
    // Clear URL params
    clearUrlParams();
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages || !localSelectedAppId) return;

    if (currentSearchParams) {
      const params: SearchParams = {
        ...currentSearchParams,
        limit: pageSize,
        offset: (newPage - 1) * pageSize,
      };
      onSearch(params, localSelectedAppId);
      // Update URL with user-entered params (not computed dates)
      setUrlSearchParams(userEnteredParams as SearchParams, localSelectedAppId, newPage, pageSize);
    }
    onPageChange?.(newPage);
    setExpandedId(null);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (!localSelectedAppId) return;

    if (currentSearchParams) {
      const params: SearchParams = {
        ...currentSearchParams,
        limit: newPageSize,
        offset: 0,
      };
      onSearch(params, localSelectedAppId);
      // Update URL with user-entered params (not computed dates)
      setUrlSearchParams(userEnteredParams as SearchParams, localSelectedAppId, 1, newPageSize);
    }
    onPageSizeChange?.(newPageSize);
    onPageChange?.(1);
    setExpandedId(null);
  };

  const handleRowClick = (log: LogEntry) => {
    setExpandedId(expandedId === log.id ? null : log.id);
  };

  const handleFilterBySession = (sessionId: string) => {
    if (!localSelectedAppId || !currentSearchParams) return;

    // Update search params with session filter (for API)
    const paramsForApi: SearchParams = {
      ...currentSearchParams,
      session_ids: [sessionId],
      limit: pageSize,
      offset: 0,
    };
    // Update user-entered params with new filter (for URL - no computed dates)
    const newUserParams = {
      ...userEnteredParams,
      session_ids: [sessionId],
    };
    setCurrentSearchParams(paramsForApi);
    setUserEnteredParams(newUserParams);
    setExpandedId(null);
    onSearch(paramsForApi, localSelectedAppId);
    onPageChange?.(1);
    // Update URL with user-entered params only
    setUrlSearchParams(newUserParams as SearchParams, localSelectedAppId, 1, pageSize);
  };

  const handleFilterByCorrelation = (correlationId: string) => {
    if (!localSelectedAppId || !currentSearchParams) return;

    // Update search params with correlation filter (for API)
    const paramsForApi: SearchParams = {
      ...currentSearchParams,
      correlation_ids: [correlationId],
      limit: pageSize,
      offset: 0,
    };
    // Update user-entered params with new filter (for URL - no computed dates)
    const newUserParams = {
      ...userEnteredParams,
      correlation_ids: [correlationId],
    };
    setCurrentSearchParams(paramsForApi);
    setUserEnteredParams(newUserParams);
    setExpandedId(null);
    onSearch(paramsForApi, localSelectedAppId);
    onPageChange?.(1);
    // Update URL with user-entered params only
    setUrlSearchParams(newUserParams as SearchParams, localSelectedAppId, 1, pageSize);
  };

  const handleFilterByInstance = (instanceId: string) => {
    if (!localSelectedAppId || !currentSearchParams) return;

    // Update search params with instance filter (for API)
    const paramsForApi: SearchParams = {
      ...currentSearchParams,
      instance_ids: [instanceId],
      limit: pageSize,
      offset: 0,
    };
    // Update user-entered params with new filter (for URL - no computed dates)
    const newUserParams = {
      ...userEnteredParams,
      instance_ids: [instanceId],
    };
    setCurrentSearchParams(paramsForApi);
    setUserEnteredParams(newUserParams);
    setExpandedId(null);
    onSearch(paramsForApi, localSelectedAppId);
    onPageChange?.(1);
    // Update URL with user-entered params only
    setUrlSearchParams(newUserParams as SearchParams, localSelectedAppId, 1, pageSize);
  };

  const handleExport = async () => {
    if (!localSelectedAppId || !currentSearchParams || !onExport) return;

    setIsExporting(true);
    try {
      await onExport(localSelectedAppId, exportFormat, currentSearchParams);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClearFiltered = async () => {
    if (!localSelectedAppId || !currentSearchParams || !onClearLogs) return;

    setIsClearing(true);
    try {
      await onClearLogs(localSelectedAppId, currentSearchParams);
      setShowClearDialog(false);
      // Re-run the search to refresh the list
      onSearch(currentSearchParams, localSelectedAppId);
    } finally {
      setIsClearing(false);
    }
  };

  const handleClearAll = async () => {
    if (!localSelectedAppId || !onClearLogs) return;

    setIsClearing(true);
    try {
      await onClearLogs(localSelectedAppId);
      setShowClearDialog(false);
      // Reset search params and refresh
      if (currentSearchParams) {
        onSearch(currentSearchParams, localSelectedAppId);
      }
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {/* App Selector */}
      {apps.length > 0 ? (
        <div
          className={cn(
            'flex items-center gap-3 rounded-xl p-4',
            'border border-[var(--color-border)] bg-[var(--color-card)]',
            'shadow-[0_1px_3px_rgba(0,0,0,0.1)]'
          )}
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--color-primary)]/10">
            <Box className="h-4 w-4 text-[var(--color-primary)]" />
          </div>
          <div className="flex flex-col gap-0.5">
            <label className="text-xs font-medium text-[var(--color-muted-foreground)]">
              Search logs in app
            </label>
            <Select
              value={localSelectedAppId}
              onChange={(e) => handleAppChange(e.target.value)}
              options={appOptions}
              className="w-64"
            />
          </div>
        </div>
      ) : (
        <div
          className={cn(
            'flex items-center gap-3 rounded-xl p-4',
            'border border-dashed border-[var(--color-border)] bg-[var(--color-card)]/50'
          )}
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--color-muted)]">
            <Box className="h-4 w-4 text-[var(--color-muted-foreground)]" />
          </div>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Create an app first to search logs
          </p>
        </div>
      )}

      {/* Search Bar */}
      {/* Pass urlSearchParams instead of currentSearchParams to avoid showing computed defaults */}
      {/* This keeps date fields empty unless user explicitly entered dates or they came from URL */}
      <SearchBar
        value={urlSearchParams ?? undefined}
        onSearch={handleSearch}
        onReset={handleReset}
        isLoading={isLoading}
        defaultRangeHours={defaultRangeHours}
      />

      {/* Actions Toolbar - only show when we have search results */}
      {currentSearchParams && total > 0 && (onExport || onClearLogs) && (
        <div
          className={cn(
            'flex items-center justify-between gap-4 rounded-lg p-3',
            'border border-[var(--color-border)] bg-[var(--color-card)]'
          )}
        >
          <span className="text-sm text-[var(--color-muted-foreground)]">
            {total.toLocaleString()} logs match current filters
          </span>

          <div className="flex items-center gap-2">
            {/* Export controls */}
            {onExport && (
              <>
                <Select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
                  options={EXPORT_FORMAT_OPTIONS}
                  className="w-24"
                />
                <Button
                  variant="outline"
                  onClick={handleExport}
                  disabled={isExporting || isLoading}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {isExporting ? 'Exporting...' : 'Export'}
                </Button>
              </>
            )}

            {/* Clear button */}
            {onClearLogs && (
              <Button
                variant="destructive"
                onClick={() => setShowClearDialog(true)}
                disabled={isClearing || isLoading}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Clear Logs
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Clear Confirmation Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear Logs</DialogTitle>
            <DialogDescription>
              Choose which logs to clear for this application. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            {currentSearchParams && (
              <Button
                variant="outline"
                onClick={handleClearFiltered}
                disabled={isClearing}
                className="justify-start"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {isClearing ? 'Clearing...' : `Clear ${total.toLocaleString()} matching logs`}
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={handleClearAll}
              disabled={isClearing}
              className="justify-start"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {isClearing ? 'Clearing...' : 'Clear ALL logs for this app'}
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearDialog(false)} disabled={isClearing}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error}
        </div>
      )}

      {/* Log Table */}
      <LogTable
        logs={logs}
        expandedId={expandedId}
        onRowClick={handleRowClick}
        onFilterSession={handleFilterBySession}
        onFilterCorrelation={handleFilterByCorrelation}
        onFilterInstance={handleFilterByInstance}
        isLoading={isLoading}
        emptyMessage={currentSearchParams ? 'No logs match your search criteria' : 'Enter search criteria and click Search'}
      />

      {/* Pagination */}
      {total > 0 && (
        <div
          className={cn(
            'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
            'rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3'
          )}
        >
          <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
            <span>
              Showing <span className="font-medium text-[var(--color-foreground)]">{startIndex}</span> -{' '}
              <span className="font-medium text-[var(--color-foreground)]">{endIndex}</span> of{' '}
              <span className="font-medium text-[var(--color-foreground)]">{total.toLocaleString()}</span> logs
            </span>
            <span className="hidden sm:inline">|</span>
            <span className="flex items-center gap-2">
              Rows per page:
              <Select
                value={String(pageSize)}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                options={PAGE_SIZE_OPTIONS}
                className="w-20"
              />
            </span>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={() => handlePageChange(1)}
              disabled={page === 1 || isLoading}
              title="First page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1 || isLoading}
              title="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-3 text-sm text-[var(--color-foreground)]">
              Page <span className="font-medium">{page}</span> of <span className="font-medium">{totalPages}</span>
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages || !hasMore || isLoading}
              title="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => handlePageChange(totalPages)}
              disabled={page === totalPages || isLoading}
              title="Last page"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LogViewer;
